/**
 * The spillway rating curve, with the documented capacity point and the
 * modelled peak plotted on it.
 *
 * WHY THIS EXISTS. Every document the team holds quotes the spillway capacity
 * as 15,717 m3/s, and the model reports discharges above that. Read cold, that
 * looks like a bug - and until 2026-09-08 the code "fixed" it by clamping the
 * discharge at 15,717, which was itself the bug: it made dQ/dH exactly zero
 * over a 3 m range and suppressed up to 13,324 m3/s of outflow.
 *
 * 15,717 m3/s is quoted AT MWL 497.58 m. It is one point on a rising curve,
 * not a ceiling. Putting the documented point and the modelled peak on the
 * same axes makes that visible rather than asserted, which is the only way an
 * examiner can check it in the ten seconds they will spend on it.
 */

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { SpillwayRating as SpillwayRatingData } from '../api/types'
import { C, fs } from '../design/tokens'

const formatCumecs = (value: number) => Math.round(value).toLocaleString()

export function SpillwayRating({ data }: { data: SpillwayRatingData }) {
  const points = data.levels_m.map((level, index) => ({
    level,
    q: data.discharge_cumecs[index],
  }))

  return (
    <section className="my-8">
      <h3 className="text-xs font-medium uppercase tracking-wider text-slate-400">
        Spillway rating &mdash; all 41 gates fully open
      </h3>
      <p className="mt-1.5 max-w-3xl text-12 leading-snug text-slate-400">
        {data.note}
      </p>

      <div className="mt-3 h-72 rounded-md border border-slate-800 bg-slate-950/40 p-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 8, right: 18, bottom: 20, left: 8 }}>
            <CartesianGrid stroke={C.grid} strokeDasharray="2 4" />
            <XAxis
              dataKey="level"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fill: C.textFaint, fontSize: fs(12) }}
              tickFormatter={(value: number) => value.toFixed(0)}
              label={{
                value: 'Reservoir level (m)',
                position: 'insideBottom',
                offset: -12,
                fill: C.textFaint,
                fontSize: fs(12),
              }}
            />
            <YAxis
              tick={{ fill: C.textFaint, fontSize: fs(12) }}
              tickFormatter={formatCumecs}
              width={62}
              label={{
                value: 'Discharge (m3/s)',
                angle: -90,
                position: 'insideLeft',
                fill: C.textFaint,
                fontSize: fs(12),
              }}
            />
            <Tooltip
              contentStyle={{
                background: C.bg,
                border: '1px solid var(--ink-grid)',
                fontSize: fs(12),
              }}
              formatter={(value: number) => [`${formatCumecs(value)} m3/s`, 'Discharge']}
              labelFormatter={(value: number) => `${Number(value).toFixed(2)} m`}
            />
            <Legend wrapperStyle={{ fontSize: fs(12), color: C.textDim }} />

            <ReferenceLine
              x={data.crest_m}
              stroke={C.textFaint}
              strokeDasharray="3 3"
              label={{
                value: `crest ${data.crest_m.toFixed(2)}`,
                angle: -90,
                position: 'insideTopLeft',
                fill: C.textFaint,
                fontSize: fs(12),
              }}
            />
            <ReferenceLine
              x={data.gate_lip_m}
              stroke={C.textFaint}
              strokeDasharray="3 3"
              label={{
                value: `gate lip ${data.gate_lip_m.toFixed(2)}`,
                angle: -90,
                position: 'insideTopLeft',
                fill: C.textFaint,
                fontSize: fs(12),
              }}
            />

            <Line
              type="monotone"
              dataKey="q"
              name="Modelled discharge"
              stroke={C.accent}
              strokeWidth={1.8}
              dot={false}
              isAnimationActive={false}
            />

            <ReferenceDot
              x={data.capacity_point.level_m}
              y={data.capacity_point.discharge_cumecs}
              r={5}
              fill={C.warn}
              stroke={C.bg}
              strokeWidth={1.5}
              label={{
                value: `documented ${formatCumecs(data.capacity_point.discharge_cumecs)}`,
                position: 'left',
                fill: C.warn,
                fontSize: fs(12),
              }}
            />
            <ReferenceDot
              x={data.modelled_peak.level_m}
              y={data.modelled_peak.discharge_cumecs}
              r={5}
              fill={C.alert}
              stroke={C.bg}
              strokeWidth={1.5}
              label={{
                value: `modelled peak ${formatCumecs(data.modelled_peak.discharge_cumecs)}`,
                position: 'right',
                fill: C.alert,
                fontSize: fs(12),
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  )
}
